import {memo, ReactElement, useEffect, useRef, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {v4 as uuidv4} from "uuid";

const VERSION = "1.1.14";

export interface CSVBoxButtonProps {
    className?: string;
    // lazy?: boolean; // Currently we don't support lazy
    loadStarted?: () => void;
    user: any;
    dynamicColumns?: any;
    options?: any;
    onReady?: () => void;
    onImport: (type: boolean, metadata?: any) => void;
    onSubmit?: (metadata: any) => void;
    onClose?: () => void;
    licenseKey: string;
    dataLocation?: string;
    customDomain?: string;
    language?: string;
    environment?: any;
    theme?: string;
    render: (launch: any, isLoading: boolean) => ReactElement;
}

function generateUrl(props: CSVBoxButtonProps): string {
    const {licenseKey} = props;
    const {dataLocation} = props;
    const {customDomain} = props;
    const {language} = props;
    const {environment} = props;
    const {theme} = props;

    let domain = customDomain ? customDomain : "app.csvbox.io";

    if(dataLocation) {
        domain = `${dataLocation}-${domain}`;
    }

    let iframeUrl = `https://${domain}/embed/${licenseKey}`;

    iframeUrl += `?library-version=${VERSION}`;
    iframeUrl += "&framework=react";

    if(dataLocation) {
        iframeUrl += "&preventRedirect";
    }

    if(language) {
        iframeUrl += "&language=" + language;
    }

    if(theme) {
        iframeUrl += "&theme=" + theme;
    }

    if(environment) {
        const env = JSON.stringify(environment).replace(/['"]/g, function(match) {
            return '\\' + match;
        });
        iframeUrl += `&env=${env}`;
    }

    return iframeUrl;
}


function InternalCSVBoxButton(props: CSVBoxButtonProps): ReactElement {
    const iframeUrl = generateUrl(props);

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const holderRef = useRef<HTMLDivElement | null>(null);
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const openOnReady = useRef<boolean>(false);

    function openModal() {
        if(iframeRef.current && holderRef.current) {
            iframeRef.current.contentWindow?.postMessage("openModal", "*");
            holderRef.current.style.display = "block";
        } else {
            openOnReady.current = true;
        }
    }


    useEffect(() => {
        const body = document.body;
        if(!body) {
            return;
        }

        const uuid = uuidv4();
        const holder = document.createElement("div");
        setStyles(holder, {
            display: "none",
            position: "fixed",
            top: "0",
            bottom: "0",
            left: "0",
            right: "0",
            "z-index": "1000"
        });
        holderRef.current = holder;

        const iframe = document.createElement("iframe");
        iframe.setAttribute("src", iframeUrl);
        iframe.frameBorder = "0";
        iframe.classList.add('csvbox-iframe');
        setStyles(iframe, {
            width: "100%",
            height: "100%",
            position: "absolute",
            top: "0",
            left: "0"
        });

        iframe.onload = () => {
            // enableInitiator();
            iframe.contentWindow?.postMessage({
                "customer": props.user ?? null,
                "dynamicColumns": props.dynamicColumns ?? null,
                "options": props.options ?? null,
                "unique_token": uuid,
            }, "*");
            props.onReady?.();

            iframeRef.current = iframe;
            setIsLoading(false);
            if(openOnReady.current) {
                openModal();
            }
        };

        const listener = (event: MessageEvent<any>) => {
            if (event.data === "mainModalHidden") {
                if (holder) {
                    holder.style.display = 'none';
                }
                props.onClose?.();
            }
            if(event.data === "uploadSuccessful") {
                props.onImport(true);
            }
            if(event.data === "uploadFailed") {
                props.onImport(false);
            }
            if(typeof event.data == "object") {

                if(event?.data?.data?.unique_token == uuid) {

                    if(event.data.type && event.data.type == "data-on-submit") {
                        let metadata = event.data.data;
                        metadata["column_mappings"] = event.data.column_mapping;
                        delete metadata["unique_token"];
                        props.onSubmit?.(metadata);
                    }
                    else if(event.data.type && event.data.type == "data-push-status") {
                        if(event.data.data.import_status == "success") {
                            if(event.data && event.data.row_data) {
                                let primary_row_data = event.data.row_data;
                                let headers = event.data.headers;
                                let rows: any[] = [];
                                let dynamic_columns_indexes = event.data.dynamicColumnsIndexes;
                                let virtual_columns_indexes = event.data.virtualColumnsIndexes || [];

                                let dropdown_display_labels_mappings = event.data.dropdown_display_labels_mappings;
                                primary_row_data.forEach((row_data: any) => {
                                    let x: any = {};
                                    let dynamic_columns: any = {};
                                    let virtual_data: any = {};
                                    row_data.data?.forEach((col: any, i: any) => {
                                        if(col == undefined){ col = "" }
                                        if(!!dropdown_display_labels_mappings[i] && !!dropdown_display_labels_mappings[i][col]) {
                                            col = dropdown_display_labels_mappings[i][col];
                                        }
                                        if(dynamic_columns_indexes.includes(i)) {
                                            dynamic_columns[headers[i]] = col;
                                        }
                                        else if(virtual_columns_indexes.includes(i)) {
                                            virtual_data[headers[i]] = col;
                                        }
                                        else{
                                            x[headers[i]] = col;
                                        }
                                    });
                                    if(row_data.unmapped_data) {
                                        x["_unmapped_data"] = row_data.unmapped_data;
                                    }
                                    if(dynamic_columns && Object.keys(dynamic_columns).length > 0) {
                                        x["_dynamic_data"] = dynamic_columns;
                                    }
                                    if(virtual_data && Object.keys(virtual_data).length > 0) {
                                        x["_virtual_data"] = virtual_data;
                                    }
                                    rows.push(x);
                                });
                                let metadata = event.data.data;
                                metadata["rows"] = rows;
                                metadata["column_mappings"] = event.data.column_mapping;
                                metadata["raw_columns"] = event.data.raw_columns;
                                metadata["ignored_columns"] = event.data.ignored_column_row;
                                delete metadata["unique_token"];
                                props.onImport(true, metadata);
                            }else{
                                let metadata = event.data.data;
                                delete metadata["unique_token"];
                                props.onImport(true, metadata);
                            }
                        }else {
                            props.onImport(false, event.data.data);
                        }
                    } else if(event.data.type && event.data.type == "csvbox-modal-hidden") {
                        if (holder) {
                            holder.style.display = 'none';
                        }
                        props.onClose?.();
                    } else if(event.data.type && event.data.type == "csvbox-upload-successful") {
                        props.onImport?.(true);
                    } else if(event.data.type && event.data.type == "csvbox-upload-failed") {
                        props.onImport?.(false);
                    }
                }
            }
        };

        window.addEventListener("message", listener, false);

        holder.appendChild(iframe);
        body.appendChild(holder);

        return () => {
            window.removeEventListener("message", listener, false);
            body.removeChild(holder);
        };
    }, [props, iframeUrl]);

    return props.render(openModal, isLoading);
}

function setStyles(el: HTMLElement, styles: Record<string, string | null>): void {
    for(const [key, value] of Object.entries(styles)) {
        if(typeof value === "string" || value === null) {
            el.style.setProperty(key, value);
        }
    }
}

function deepEquals(a: any, b: any): boolean {
    if(a === b) {
        return true;
    }

    if(a === null || b === null || a === undefined || b === undefined) {
        return a === b;
    }

    if(typeof a !== typeof b) {
        return false;
    }

    if(typeof a !== 'object') {
        return a === b;
    }

    if(Array.isArray(a) && Array.isArray(b)) {
        if(a.length !== b.length) {
            return false;
        }
        for(let i = 0; i < a.length; i++) {
            if(!deepEquals(a[i], b[i])) {
                return false;
            }
        }
        return true;
    }

    if(Array.isArray(a) || Array.isArray(b)) {
        return false;
    }

    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if(keysA.length !== keysB.length) {
        return false;
    }

    for(const key of keysA) {
        if(!keysB.includes(key)) {
            return false;
        }
        if(!deepEquals(a[key], b[key])) {
            return false;
        }
    }

    return true;
}

export const CSVBoxButton = memo(InternalCSVBoxButton, (prev, next) => {
    return deepEquals(prev, next);
});